import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../Stylesheets/LandingPage.css';

const featureCards = [
  {
    eyebrow: 'Pipeline clarity',
    title: 'Track every lead without the clutter',
    description:
      'Keep contacts, notes, follow-ups, and deal stages in one simple workflow your whole team can actually use.',
  },
  {
    eyebrow: 'Bot-ready workflow',
    title: 'Connect your own AI agent instead of paying for bundled AI',
    description:
      'Use your preferred bot or AI agent with your CRM data so you can automate follow-up and workflows without stacking multiple AI subscriptions.',
  },
  {
    eyebrow: 'Lean operations',
    title: 'Built for low-cost growth',
    description:
      'Start with a 14-day free trial, avoid bloated enterprise pricing, and keep every seat at $7 a month after trial.',
  },
];

const benefits = [
  'Contact and company records',
  'Tasks, reminders, and next-step tracking',
  'Ready for AI agents and bot automations',
  'Shared pipeline visibility for small teams',
  'Fast setup with no long onboarding cycle',
];

const stats = [
  { value: '14', label: 'days free before billing starts' },
  { value: '1', label: 'CRM that is ready to connect with your own bots' },
  { value: '0', label: 'extra AI subscriptions required to automate your workflow' },
];

function LandingPage() {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const handleGetStarted = () => {
    navigate('/signUp');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <div className="landing-page-shell">
      <section className="landing-hero">
        <div className="landing-hero__backdrop" />
        <div className="landing-hero__content">
          <div className="landing-hero__copy">
            <img
              src="/assets/whiteSgnl.png"
              alt="Signl CRM"
              className="landing-hero__logo"
            />
            <span className="landing-badge">CRM for lean teams</span>
            <h1>Run your customer pipeline on a low-cost CRM that is ready for AI agents.</h1>
            <p className="landing-hero__lede">
              A modern CRM for small businesses that need contact management,
              follow-up discipline, and clear pipeline visibility without
              paying enterprise software prices or overpaying for AI bundled into the CRM.
            </p>

            <div className="landing-hero__actions">
              <button className="landing-button landing-button--primary" onClick={handleGetStarted}>
                Start 14-Day Free Trial
              </button>
              <button className="landing-button landing-button--secondary" onClick={handleSignIn}>
                Sign in
              </button>
            </div>

            <ul className="landing-hero__list">
              {benefits.map((benefit) => (
                <li key={benefit}>{benefit}</li>
              ))}
            </ul>
          </div>

          <div className="landing-hero__panel">
            <div className="landing-price-card">
              <span className="landing-price-card__label">Starter plan</span>
              <div className="landing-price-card__price">
                <strong>$7</strong>
                <span>per user each month after 14 free days</span>
              </div>
              <p>
                Everything a growing team needs to organize leads, manage
                follow-ups, and keep sales activity moving while staying flexible enough to connect your own bot stack.
              </p>

              <div className="landing-price-card__stack">
                <div>
                  <span>Lead capture</span>
                  <strong>Centralized</strong>
                </div>
                <div>
                  <span>AI readiness</span>
                  <strong>Bot-friendly</strong>
                </div>
                <div>
                  <span>Cost control</span>
                  <strong>No AI bloat</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-stats">
        <div className="landing-stats__grid">
          {stats.map((stat) => (
            <article key={stat.label} className="landing-stat-card">
              <h2>{stat.value}</h2>
              <p>{stat.label}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-section__heading">
          <span className="landing-kicker">Why teams switch</span>
          <h2>Modern where it matters. Affordable where it counts.</h2>
          <p>
            Signl is built around the core CRM jobs most small teams need:
            keep the database clean, stay on top of follow-up, make the
            pipeline easy to read, and plug in outside AI when you want it.
          </p>
        </div>

        <div className="landing-feature-grid">
          {featureCards.map((feature) => (
            <article key={feature.title} className="landing-feature-card">
              <span>{feature.eyebrow}</span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section landing-section--split">
        <div className="landing-section__heading landing-section__heading--left">
          <span className="landing-kicker">How it works</span>
          <h2>Get organized in days, then connect automation on your terms.</h2>
          <p>
            This setup is positioned for businesses that want a dependable CRM
            without a long implementation project, oversized monthly bill, or pressure to buy AI features inside the platform.
          </p>
        </div>

        <div className="landing-steps">
          <article>
            <strong>01</strong>
            <h3>Import your contacts</h3>
            <p>Bring in leads, companies, and current customers into one central workspace.</p>
          </article>
          <article>
            <strong>02</strong>
            <h3>Assign next actions</h3>
            <p>Turn every conversation into a tracked follow-up so deals keep moving.</p>
          </article>
          <article>
            <strong>03</strong>
            <h3>Layer in your bot</h3>
            <p>Connect your preferred AI agent or automation instead of paying for multiple AI subscriptions inside expensive CRM suites.</p>
            <p>Every new account starts with a 14-day free trial, then stays predictable at $7 per user each month.</p>
          </article>
        </div>
      </section>

      <section className="landing-cta">
        <div>
          <img
            src="/assets/blackSgnl.png"
            alt="Signl CRM"
            className="landing-cta__logo"
          />
          <span className="landing-kicker">Low-cost CRM, bot-ready stack</span>
          <h2>Use the CRM you need and connect the AI you actually want.</h2>
          
        </div>
        <button className="landing-button landing-button--primary" onClick={handleGetStarted}>
          Create your account
        </button>
      </section>

      <footer className="landing-footer">
        <img
          src="/assets/blackSgnl.png"
          alt="Signl CRM"
          className="landing-footer__logo"
        />
        <p>© {year} Signl CRM. Built for affordable customer relationship management and ready for AI-powered automation.</p>
      </footer>
    </div>
  );
}

export default LandingPage;
